import { useApi } from "@/hooks/use-api";
import { useAppState } from "@/store/useApp";
import { DocumentInfo, DocumentView } from "@/types/document";
import { useFlow } from "@frigade/react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Documents } from "./Documents";
import { config } from "@/config";
import { matchesDocumentView } from "./documentViewFilters";

interface DocumentsContainerProps {
    currentView: DocumentView;
}

export const DocumentsContainer = ({ currentView }: DocumentsContainerProps) => {
  const courses = useAppState((state) => state.courses || []);
  const assessments = useAppState((state) => state.assessments || []);
  const documents = useAppState((state) => state.documents);
  const { fetchDocumentData: fetchDocumentWithFeedback } = useApi();
  const navigate = useNavigate();
  const [loadingDocId, setLoadingDocId] = useState<string | null>(null);
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { flow } = useFlow(config.frigade.flows.demoAssignmentHint);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleOpenDocument = async (doc: DocumentInfo) => {
    if (doc.status === "importing") return;

    if (flow && !flow?.isCompleted) {
      flow.complete();
    }

    setLoadingDocId(doc.id);
    try {
      await fetchDocumentWithFeedback(doc.id);
      navigate(`/documents/${doc.id}`);
    } finally {
      setLoadingDocId(null);
    }
  };

  const assessmentIdToCourseId = useMemo(() => {
    const mapping = new Map<string, string>();
    assessments.forEach((a) => {
      mapping.set(a.id, a.courseId);
    });
    return mapping;
  }, [assessments]);

  const filteredDocuments = useMemo(
    () => {
      const filtered = (documents || [])
        .filter((doc) => matchesDocumentView(doc, currentView))
        .filter((doc) => {
          if (selectedCourseIds.length === 0) return true;
          if (!doc.assessmentId) return false;
          const courseId = assessmentIdToCourseId.get(doc.assessmentId);
          return courseId ? selectedCourseIds.includes(courseId) : false;
        })
        .filter((doc) => {
          if (!searchQuery.trim()) return true;
          const searchTerm = searchQuery.toLowerCase();
          return (
            doc.title.toLowerCase().includes(searchTerm) ||
            doc.studentId?.toLowerCase().includes(searchTerm) ||
            doc.fileName?.toLowerCase().includes(searchTerm)
          );
        });

      // Sort documents for ready view
      if (currentView === "ready") {
        return filtered.sort((a, b) => {
          // InProgress documents on top
          if (a.status === "in_progress" && b.status !== "in_progress") return -1;
          if (b.status === "in_progress" && a.status !== "in_progress") return 1;

          // Ready documents next
          if (a.status === "ready" && b.status !== "ready") return -1;
          if (b.status === "ready" && a.status !== "ready") return 1;

          // Both are importing documents, sort by progress
          if (a.status === "importing" && b.status === "importing") {
            const aProgress = a.progress?.current || 0;
            const bProgress = b.progress?.current || 0;

            // Documents with progress > 0 come before documents with progress = 0
            if (aProgress > 0 && bProgress === 0) return -1;
            if (bProgress > 0 && aProgress === 0) return 1;

            // If both have progress > 0 or both have progress = 0, sort by progress value (descending)
            return bProgress - aProgress;
          }

          return 0;
        });
      }

      return filtered;
    },
    [
      documents,
      currentView,
      selectedCourseIds,
      assessmentIdToCourseId,
      searchQuery,
    ]
  );

  const coursesExcludingOnboarding = courses.filter(
    (course) => course.metaType !== "onboarding"
  );

  return (
    <Documents
      currentView={currentView}
      filteredDocuments={filteredDocuments}
      loadingDocId={loadingDocId}
      selectedCourseIds={selectedCourseIds}
      searchQuery={searchQuery}
      coursesExcludingOnboarding={coursesExcludingOnboarding}
      onCourseFilterChange={setSelectedCourseIds}
      onSearchChange={handleSearchChange}
      onOpenDocument={handleOpenDocument}
    />
  );
};
